import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import { Link } from 'react-router-dom'
import rawgApi from '../../data/api/rawg/rawg-api'
import savedGameApi from '../../data/api/saved-game-api'
import { getLoggedInUser } from '../../data/auth/auth-storage'

import WindowsLogo from '../../assets/platforms/windows.svg'
import PlayStationLogo from '../../assets/platforms/playstation.svg'
import XboxLogo from '../../assets/platforms/xbox.svg'
import NintendoLogo from '../../assets/platforms/nintendo.svg'
import AppleLogo from '../../assets/platforms/apple.svg'
import AndroidLogo from '../../assets/platforms/android.svg'
import LinuxLogo from '../../assets/platforms/linux.svg'
import IOSLogo from '../../assets/platforms/ios.svg'

interface RawgScreenshot {
  id: number
  image: string
}

interface RawgPlatform {
  platform: {
    id: number
    name: string
    slug: string
  }
}

interface RawgGame {
  id: number
  name: string
  released: string
  background_image: string
  rating: number
  short_screenshots?: RawgScreenshot[]
  parent_platforms?: RawgPlatform[]
    slug?: string
  description_raw?: string
  esrb_rating?: { name?: string } | null
  age_ratings?: Array<{
    rating?: {
      name?: string
      slug?: string
    } | null
  }>
}

interface RawgGenre {
  id: number
  name: string
  slug: string
}

const ADULT_CONTENT_TERMS = [
  'adult only', 'adults only', 'mature', 'explicit', 'nudity', 'sex',
  'sexual', 'porn', 'nsfw', 'erotic', 'hentai', 'fetish', 'strip',
  'xxx', '18+', 'erotica', 'gore', 'pregnation',
]

const isFamilyFriendlyGame = (game: RawgGame) => {
  const ratingName = game.esrb_rating?.name?.toLowerCase() ?? ''

  const ageRatingNames = (game.age_ratings || [])
    .map(entry => entry.rating?.name?.toLowerCase() ?? entry.rating?.slug?.toLowerCase() ?? '')
    .filter(Boolean)

  const adultMatch = [ratingName, ...ageRatingNames].some(value =>
  ['adults only', 'adult only', '18+', 'ao'].some(term => value.includes(term))
)

  if (adultMatch) return false

  const searchableText =
    `${game.name ?? ''} ${game.description_raw ?? ''} ${game.slug ?? ''}`.toLowerCase()

  return !ADULT_CONTENT_TERMS.some(term => searchableText.includes(term))
}

const GamesPage = (): React.JSX.Element => {
  const [searchQuery, setSearchQuery] = useState(
    sessionStorage.getItem('gameSearchQuery') || ''
  )

  const [games, setGames] = useState<RawgGame[]>(() => {
    const savedGames = sessionStorage.getItem('gameSearchResults')

    return savedGames ? JSON.parse(savedGames) : []
  })

const [loading, setLoading] = useState(false)
const [error, setError] = useState('')

const [page, setPage] = useState(() => {
  return Number(sessionStorage.getItem('gameSearchPage') || '1')
})

const [selectedGenre, setSelectedGenre] = useState(
  sessionStorage.getItem('gameGenre') || ''
)

const [genres, setGenres] = useState<RawgGenre[]>([])

useEffect(() => {
  const loadGenres = async () => {
    try {
      const response = await rawgApi.getGenres()

      const parsedData =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data

      setGenres(parsedData.results || [])
    } catch {
      setError('Unable to load genres.')
    }
  }

  loadGenres()
}, [])

useEffect(() => {
  sessionStorage.setItem('gameSearchPage', page.toString())
}, [page])

useEffect(() => {
  sessionStorage.setItem(
    'gameGenre',
    selectedGenre
  )
}, [selectedGenre])

const [activeScreenshots, setActiveScreenshots] = useState<
  Record<number, number>
>({})

const [libraryCounts, setLibraryCounts] = useState<
  Record<number, number>
>({})

  useEffect(() => {
    sessionStorage.setItem('gameSearchQuery', searchQuery)
  }, [searchQuery])

  useEffect(() => {
    sessionStorage.setItem(
      'gameSearchResults',
      JSON.stringify(games)
    )
  }, [games])

  useEffect(() => {
  sessionStorage.setItem(
    'gameSearchPage',
    page.toString()
  )
}, [page])

  useEffect(() => {
    const savedScroll = sessionStorage.getItem(
      'gameScrollPosition'
    )

    if (savedScroll) {
      setTimeout(() => {
        window.scrollTo(0, Number(savedScroll))
      }, 100)
    }
  }, [])

  useEffect(() => {
    const loadLibraryCounts = async () => {
      const counts: Record<number, number> = {}

      for (const game of games) {
        try {
          const response = await savedGameApi.getLibraryCount(
            game.id.toString()
          )

          counts[game.id] = response.libraryCount
        } catch {
          counts[game.id] = 0
        }
      }

      setLibraryCounts(counts)
    }

    if (games.length > 0) {
      loadLibraryCounts()
    }
  }, [games])

    const handleSearch = async (pageNumber = page) => {
  if (!searchQuery.trim()) {
    setError('Please enter a game title.')
    return
  }

  try {
    setLoading(true)
    setError('')
    setActiveScreenshots({})

    const response = await rawgApi.searchGames(searchQuery, pageNumber, selectedGenre)

    const parsedData =
      typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data

    const results: RawgGame[] = parsedData.results || []
    const allowAdultContent =
      getLoggedInUser()?.allowAdultContent ?? false

    if (allowAdultContent) {
      setGames(results)
    } else {
      const detailedGames = await Promise.all(
        results.map(async (game) => {
          try {
            const response = await rawgApi.getGameDetails(game.id)

            const details =
              typeof response.data === 'string'
                ? JSON.parse(response.data)
                : response.data

            return { ...game, ...details }
          } catch {
            return game
          }
        })
      )

      setGames(detailedGames.filter(isFamilyFriendlyGame))
    }

    setPage(pageNumber)
    window.scrollTo(0, 0)
  } catch {
    setError('Unable to search for games.')
  } finally {
    setLoading(false)
  }
}

  const handleImageMouseMove = (
    event: React.MouseEvent<HTMLDivElement>,
    game: RawgGame
  ) => {
    const screenshots = game.short_screenshots || []

    if (screenshots.length <= 1) {
      return
    }

    const imageArea =
      event.currentTarget.getBoundingClientRect()

    const mousePosition =
      event.clientX - imageArea.left

    const sectionWidth =
      imageArea.width / screenshots.length

    const screenshotIndex = Math.min(
      Math.floor(mousePosition / sectionWidth),
      screenshots.length - 1
    )

    setActiveScreenshots((current) => ({
      ...current,
      [game.id]: screenshotIndex
    }))
  }

  const handleImageMouseLeave = (gameId: number) => {
    setActiveScreenshots((current) => ({
      ...current,
      [gameId]: 0
    }))
  }

  const handleAddToLibrary = async (game: RawgGame) => {
  const userId = Number(localStorage.getItem('userId'))

  if (!userId) {
    setError(
      'Please log in before adding games to your library.'
    )
    return
  }

  try {
    await savedGameApi.saveGame({
      userId,
      gameId: game.id.toString(),
      title: game.name,
      imageUrl: game.background_image,
      releaseDate: game.released,
      rating: game.rating,
      status: 'Backlog',
      isFavorite: false
    })

    setError('')
    alert('Game added to library!')

    const response = await savedGameApi.getLibraryCount(
      game.id.toString()
    )

    setLibraryCounts((current) => ({
      ...current,
      [game.id]: response.libraryCount
    }))
  } catch (err: any) {
    console.error(err)

    if (err.response?.status === 400) {
      alert('This game is already in your library.')
    } else {
      setError('Failed to add game to library.')
    }
  }
}

const platformIconStyle = {
  filter: 'brightness(0) invert(1)',
  opacity: 0.9
}

  const getPlatformIcon = (
    slug: string
  ): React.JSX.Element | null => {
    switch (slug) {
      case 'pc':
        return (
          <img
            src={WindowsLogo}
            alt="Windows"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      case 'playstation':
        return (
          <img
            src={PlayStationLogo}
            alt="PlayStation"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      case 'xbox':
        return (
          <img
            src={XboxLogo}
            alt="Xbox"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      case 'nintendo':
        return (
          <img
            src={NintendoLogo}
            alt="Nintendo"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      case 'mac':
        return (
          <img
            src={AppleLogo}
            alt="Mac"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      case 'android':
        return (
          <img
            src={AndroidLogo}
            alt="Android"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      case 'linux':
        return (
          <img
            src={LinuxLogo}
            alt="Linux"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      case 'ios':
        return (
          <img
            src={IOSLogo}
            alt="iOS"
            width={20}
            height={20}
            style={platformIconStyle}
          />
        )

      default:
        return null
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        p: 4,
        background:
          'linear-gradient(180deg, #0b1220 0%, #0f1724 100%)',
        color: 'white'
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Paper
          elevation={4}
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            p: 4,
            borderRadius: 4,
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              sx={{ color: 'white' }}
            >
              Search Games
            </Typography>

            <Typography
              variant="h6"
              sx={{ color: 'rgba(255,255,255,0.85)' }}
            >
              Discover your next adventure using the RAWG API.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 5,
              flexDirection: {
                xs: 'column',
                sm: 'row'
              },
              alignItems: 'center'
            }}
          >
            <TextField
              placeholder="Search game title"
              value={searchQuery}
              onChange={(event) =>
                setSearchQuery(event.target.value)
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearch(1)
                }
              }}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: 2
                }
              }}
            />

    
            <FormControl
                  sx={{
                  width: 220,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white'
                  }
                }}
                >
                  

                  <Select
                  value={selectedGenre}
                  displayEmpty
                  onChange={(event) =>
                    setSelectedGenre(event.target.value)
                  }
                >
                    <MenuItem value="">All Genres</MenuItem>

                      {genres.map((genre) => (
                        <MenuItem
                          key={genre.id}
                          value={genre.slug}
                        >
                          {genre.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

            <Button
              variant="contained"
              onClick={() => handleSearch(1)}
              sx={{
                px: 4,
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#2563eb'
                }
              }}
            >
              Search
            </Button>
          </Box>

          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                my: 4
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: 'center'
            }}
          >
            {games.map((game) => {
              const screenshots =
                game.short_screenshots || []

              const activeIndex =
                activeScreenshots[game.id] || 0

              const currentImage =
                screenshots[activeIndex]?.image ||
                game.background_image

              return (
                <Card
                    key={game.id}
                    sx={{
                      width: 300,
                      minHeight: 390,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',

                      background:
                        'linear-gradient(180deg, rgba(36,58,88,0.96), rgba(24,40,64,0.96))',
                      border: '1px solid rgba(148,163,184,0.35)',
                      color: '#f8fbff',

                      transition: '0.2s ease',

                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.35)'
                      }
                    }}
                  >
                  {currentImage && (
                    <Box
                      onMouseMove={(event) =>
                        handleImageMouseMove(event, game)
                      }
                      onMouseLeave={() =>
                        handleImageMouseLeave(game.id)
                      }
                      sx={{
                        position: 'relative',
                        height: 170,
                        backgroundColor: '#111827',
                        cursor:
                          screenshots.length > 1
                            ? 'pointer'
                            : 'default'
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={currentImage}
                        alt={game.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />

                      {screenshots.length > 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 10,
                            right: 10,
                            bottom: 8,
                            display: 'flex',
                            gap: 0.5
                          }}
                        >
                          {screenshots.map(
                            (screenshot, index) => (
                              <Box
                                key={screenshot.id}
                                sx={{
                                  height: 5,
                                  flex: 1,
                                  borderRadius: 4,
                                  backgroundColor:
                                    index === activeIndex
                                      ? 'white'
                                      : 'rgba(255,255,255,0.45)',
                                  boxShadow:
                                    '0 1px 3px rgba(0,0,0,0.45)'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexGrow: 1
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        minHeight: 64,
                        color: '#f8fbff'
                      }}
                    >
                      {game.name}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        opacity: 0.9
                      }}
                    >
                      {game.parent_platforms?.map(
                        (platform) => {
                          const icon = getPlatformIcon(
                            platform.platform.slug
                          )

                          if (!icon) {
                            return null
                          }

                          return (
                            <Box
                              key={platform.platform.id}
                              title={
                                platform.platform.name
                              }
                              sx={{
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {icon}
                            </Box>
                          )
                        }
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: '#c2cedd' }}
                    >
                      Released:{' '}
                      {game.released
                        ? new Date(game.released).toLocaleDateString('en-US', {month: 'numeric',day: 'numeric', year: 'numeric'
                          })
                        : 'Unknown'}
                    </Typography>

                    <Typography
                      variant="body2"
                       sx={{ color: '#c2cedd' }}
                    >
                      Rating: {game.rating || 'N/A'}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: '#c2cedd' }}
                    >
                      In {libraryCounts[game.id] ?? 0}{' '}
                      {(libraryCounts[game.id] ?? 0) === 1
                        ? 'library'
                        : 'libraries'}
                    </Typography>

                    <Button
                      component={Link}
                      to={`/games/${game.id}`}
                      variant="contained"
                      fullWidth
                      onClick={() =>
                        sessionStorage.setItem(
                          'gameScrollPosition',
                          window.scrollY.toString()
                        )
                      }
                      sx={{
                        mt: 'auto',
                        mb: 1,
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#2563eb'
                        }
                      }}
                    >
                      View Details
                    </Button>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() =>
                        handleAddToLibrary(game)
                      }
                      sx={{
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#2563eb'
                        }
                      }}
                    >
                      Add to Library
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </Box>

            {games.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  mt: 4
                }}
              >
                <Button
                  variant="contained"
                  disabled={page === 1 || loading}
                  onClick={() => handleSearch(page - 1)}
                >
                  Previous
                </Button>

                <Typography sx={{ color: 'white' }}>
                  Page {page}
                </Typography>

                <Button
                  variant="contained"
                  disabled={loading}
                  onClick={() => handleSearch(page + 1)}
                >
                  Next
                </Button>
              </Box>
            )}

            </Paper>
      </Box>
    </Box>
  )
}

export default GamesPage